import type { ReactNode } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router';
import { hasCapability, useSession } from '../session/session';
import { BRAND, surfaceGradient } from './tokens';

/**
 * A person's round photo beside their name, wherever a name appears (management, 22 October
 * 2026). The photo is read with `hr.employee_photo:view` — by employee, or by the user who signs
 * in as them — and a session without it asks for nothing and shows initials. A person with no
 * photo shows initials too: the image fails and the avatar falls back to its letters.
 */

/**
 * Up to two initials: the first and last word of the name. A label "E0012 — Sara Ali" gives the
 * name's initials, not the code's.
 */
export function initials(name: string): string {
  const bare = name.split(' — ').pop() ?? name;
  const words = bare.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export interface PersonRef {
  readonly name: string;
  /** The employee, when the name comes with one. */
  readonly employeeId?: string | null;
  /** Otherwise the user who signs in as them. */
  readonly userId?: string | null;
}

function photoUrl({ employeeId, userId }: PersonRef): string | undefined {
  if (employeeId != null && employeeId !== '') {
    return `/api/v1/hr/employees/${encodeURIComponent(employeeId)}/photo`;
  }
  if (userId != null && userId !== '') {
    return `/api/v1/hr/users/${encodeURIComponent(userId)}/photo`;
  }
  return undefined;
}

/** The round photo alone, decorative: the name beside it is what is read out. */
export function PersonAvatar({ size = 28, ...person }: PersonRef & { size?: number }) {
  const session = useSession();
  const src = hasCapability(session.data, 'hr.employee_photo:view') ? photoUrl(person) : undefined;
  return (
    <Avatar
      aria-hidden
      src={src}
      alt=""
      slotProps={{ img: { loading: 'lazy', draggable: false } }}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        fontSize: `${(size * 0.4).toFixed(1)}px`,
        fontWeight: 700,
        color: BRAND.mint,
        backgroundImage: surfaceGradient(),
      }}
    >
      {/* Drawn by CSS, so the letters are not part of the page's text: copying a row or reading
          an element's text gives the name once, not initials and name. */}
      <Box
        component="span"
        data-initials={initials(person.name)}
        sx={{ '&::before': { content: 'attr(data-initials)' } }}
      />
    </Avatar>
  );
}

/**
 * The photo and the name on one line; the name is a link when `to` is given. `children` replaces
 * the plain name (a link of the page's own, say) while keeping the photo beside it.
 */
export function PersonName({
  to,
  size = 24,
  children,
  ...person
}: PersonRef & { to?: string | null; size?: number; children?: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        maxWidth: '100%',
        verticalAlign: 'middle',
      }}
    >
      <PersonAvatar size={size} {...person} />
      <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {children ??
          (to == null ? (
            person.name
          ) : (
            <Link component={RouterLink} to={to}>
              {person.name}
            </Link>
          ))}
      </Box>
    </Box>
  );
}
